import React, { useEffect, useMemo, useState } from "react";
import { Button, EmptyState, Modal } from "@redipos/ui";
import { Loader2, Search } from "lucide-react";
import type { ConsoleTenantListItem, SupportThreadRow } from "@redipos/contracts";
import { operacionesApi } from "../../operaciones/api.js";

type Props = {
  open: boolean;
  onClose: () => void;
  existingThreads: SupportThreadRow[];
  onSelect: (tenant: ConsoleTenantListItem) => void;
};

function tenantSearchHaystack(t: ConsoleTenantListItem): string {
  return [
    t.name,
    t.slug,
    t.razonSocial,
    t.ruc,
    t.owner?.email,
    t.owner?.usuario,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export default function StartSupportConversationModal({
  open,
  onClose,
  existingThreads,
  onSelect,
}: Props): React.ReactElement {
  const [tenants, setTenants] = useState<ConsoleTenantListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const existingIds = useMemo(
    () => new Set(existingThreads.map((t) => t.id).filter(Boolean) as string[]),
    [existingThreads],
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
      setError("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    operacionesApi
      .tenants()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setTenants(
          list.filter((t) => t.isActive && t.slug !== "redipos"),
        );
        setError("");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTenants([]);
        setError(e instanceof Error ? e.message : "No se pudieron cargar los clientes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...tenants].sort((a, b) => a.name.localeCompare(b.name, "es"));
    if (!q) return sorted;
    return sorted.filter((t) => tenantSearchHaystack(t).includes(q));
  }, [tenants, query]);

  return (
    <Modal open={open} onClose={onClose} title="Iniciar conversación" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-fg-subtle">
          Elegí un cliente para escribirle por soporte. Si nunca chateó, se creará la conversación al
          enviar el primer mensaje.
        </p>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, RUC, slug o email..."
            className="min-h-11 w-full rounded-input border border-border bg-surface-elevated py-2 pl-10 pr-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            autoFocus
          />
        </div>

        {error ? (
          <div className="rounded-input border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        ) : null}

        <div className="max-h-[min(50vh,22rem)] overflow-y-auto rounded-input border border-border">
          {loading ? (
            <div className="flex items-center gap-2 px-4 py-8 text-sm text-fg-subtle">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando clientes...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6">
              <EmptyState message={query ? "Ningún cliente coincide con la búsqueda." : "No hay clientes activos."} />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((tenant) => {
                const hasThread = existingIds.has(tenant.id);
                return (
                  <li key={tenant.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(tenant)}
                      className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-fg">{tenant.name}</p>
                        <p className="mt-0.5 truncate text-xs text-fg-subtle">
                          {tenant.owner?.email || tenant.slug || tenant.id}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                          hasThread
                            ? "border border-border bg-surface-muted text-fg-subtle"
                            : "bg-brand-100 text-brand-800"
                        }`}
                      >
                        {hasThread ? "Con chat" : "Nuevo"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </Modal>
  );
}
